import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { PPPC_SERVICE_GROUPS } from "@/types/pppc";
import { Plus, Wrench } from "lucide-react";
import { CustomServiceDialog } from "./CustomServiceDialog";

interface AddPermissionMenuProps {
  /** Keys already configured — they are hidden from the picker. */
  existingKeys: string[];
  onAdd: (key: string, label?: string) => void;
  disabled?: boolean;
}

export const AddPermissionMenu = ({
  existingKeys,
  onAdd,
  disabled,
}: AddPermissionMenuProps) => {
  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const existingSet = new Set(existingKeys);

  const handleSelect = (key: string) => {
    setOpen(false);
    onAdd(key);
  };

  const handleCustomSubmit = (key: string, label: string) => {
    setCustomOpen(false);
    onAdd(key, label);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" size="sm" disabled={disabled}>
            <Plus className="h-4 w-4 mr-1.5" />
            Add Permission
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[360px]" align="start">
          <Command>
            <CommandInput placeholder="Search privacy services..." />
            <CommandList className="max-h-[340px]">
              <CommandEmpty>No matching services.</CommandEmpty>
              {PPPC_SERVICE_GROUPS.map((group) => {
                const available = group.services.filter(
                  (s) => !existingSet.has(s.key),
                );
                if (available.length === 0) return null;
                return (
                  <CommandGroup key={group.groupLabel} heading={group.groupLabel}>
                    {available.map((svc) => (
                      <CommandItem
                        key={svc.key}
                        value={`${svc.label} ${svc.key} ${svc.description}`}
                        onSelect={() => handleSelect(svc.key)}
                        className="flex flex-col items-start gap-0.5"
                      >
                        <span className="text-sm font-medium">{svc.label}</span>
                        <span className="text-xs text-muted-foreground">
                          {svc.description}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                );
              })}
              <CommandSeparator />
              <CommandGroup>
                <CommandItem
                  value="custom service tcc key advanced"
                  onSelect={() => {
                    setOpen(false);
                    setCustomOpen(true);
                  }}
                >
                  <Wrench className="h-3.5 w-3.5 mr-2" />
                  Custom service…
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <CustomServiceDialog
        open={customOpen}
        onOpenChange={setCustomOpen}
        existingKeys={existingKeys}
        onSubmit={handleCustomSubmit}
      />
    </>
  );
};
